import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../hooks/useApi";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Clock, MailWarning, RotateCcw, Ban, ShoppingCart, Loader2 } from "lucide-react";

const STATUS_META = {
  checkout_initiated: { label: "Checkout Initiated", icon: ShoppingCart, style: "text-foreground/70 border-foreground/35" },
  payment_confirmed: { label: "Payment Confirmed", icon: Clock, style: "text-foreground/70 border-foreground/35" },
  enrollment_awaiting_activation: { label: "Awaiting Activation", icon: MailWarning, style: "text-foreground border-foreground border-dashed" },
  activation_expired: { label: "Activation Expired", icon: AlertTriangle, style: "text-foreground border-foreground border-dashed" },
  enrollment_activated: { label: "Activated", icon: CheckCircle2, style: "text-foreground border-foreground" },
  refunded_or_disputed: { label: "Refunded / Disputed", icon: Ban, style: "text-foreground/70 border-foreground/35" },
  fulfillment_intervention_required: { label: "Needs Intervention", icon: AlertTriangle, style: "text-foreground border-foreground" },
};

const RESENDABLE = new Set(["payment_confirmed", "enrollment_awaiting_activation", "activation_expired", "fulfillment_intervention_required"]);

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.payment_confirmed;
  const Icon = meta.icon;
  return (
    <span data-testid={`tms-claim-status-${status}`} className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border px-3 py-1 ${meta.style}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function ClaimRow({ claim, onResend, resendingId }) {
  const busy = resendingId === claim.session_id;
  return (
    <div className="border border-border p-4 mb-3 flex flex-wrap items-center gap-4 justify-between" data-testid={`tms-claim-row-${claim.session_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate" data-testid={`tms-claim-email-${claim.session_id}`}>{claim.email || "Email not yet provided"}</p>
        <p className="text-xs text-foreground/60 font-mono truncate">{claim.session_id}</p>
        <p className="text-xs text-foreground/70 mt-1">{claim.latest_event}{claim.created_at ? ` · opened ${new Date(claim.created_at).toLocaleString()}` : ""}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {claim.amount_cents != null && <span className="text-sm text-foreground/80">${(claim.amount_cents / 100).toFixed(2)}</span>}
        <StatusBadge status={claim.lifecycle_status} />
        {RESENDABLE.has(claim.lifecycle_status) && (
          <button
            data-testid={`tms-claim-resend-btn-${claim.session_id}`}
            onClick={() => onResend(claim.session_id)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors disabled:opacity-40"
          >
            <RotateCcw className="w-3 h-3" /> {busy ? "Sending…" : "Resend Link"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminMuscleSchool() {
  const api = useApi();
  const qc = useQueryClient();
  const [resendingId, setResendingId] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-tms-claims"],
    queryFn: async () => (await api.get("/admin/muscle-school/claims")).data,
    refetchInterval: 15000,
  });

  const resend = async (sessionId) => {
    setResendingId(sessionId);
    try {
      await api.post(`/admin/muscle-school/claims/${sessionId}/resend`);
      toast.success("Activation link resent to the purchaser.");
      qc.invalidateQueries({ queryKey: ["admin-tms-claims"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not resend.");
    } finally {
      setResendingId(null);
    }
  };

  const claims = data?.claims || [];
  const abandoned = data?.abandoned_checkouts || [];
  const counts = claims.reduce((acc, c) => { acc[c.lifecycle_status] = (acc[c.lifecycle_status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-muscle-school-page">
      <h1 className="font-display text-display-xs mb-2">The Muscle School — Enrollments</h1>
      <p className="text-xs text-foreground/60 mb-6">
        Purchase-first by design — no lead is captured before checkout, so the earliest stage below is a Stripe checkout that was opened but never paid.
      </p>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-foreground/60 py-10" data-testid="tms-claims-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading enrollments…
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-sm text-foreground border border-border p-4 mb-6" data-testid="tms-claims-error">
          Could not load Muscle School enrollments. Try refreshing the page.
        </p>
      )}

      {!isLoading && !isError && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <div key={key} className="bg-surface border border-border p-4" data-testid={`tms-stat-${key}`}>
                <p className="text-xs uppercase text-white/70">{meta.label}</p>
                <p className="font-subtitle text-2xl mt-1">{key === "checkout_initiated" ? abandoned.length : (counts[key] || 0)}</p>
              </div>
            ))}
          </div>

          <h2 className="text-sm uppercase tracking-wide text-white/70 mb-3">Paid Enrollments</h2>
          {claims.length === 0 && <p className="text-white/70 text-sm py-4 mb-4" data-testid="tms-claims-empty">No Muscle School purchases yet.</p>}
          {claims.map((c) => <ClaimRow key={c.session_id} claim={c} onResend={resend} resendingId={resendingId} />)}

          {abandoned.length > 0 && (
            <details className="mt-10" data-testid="tms-abandoned-section">
              <summary className="text-sm uppercase tracking-wide text-white/70 mb-3 cursor-pointer">
                Checkout Initiated, Not Yet Paid ({abandoned.length})
              </summary>
              <div className="mt-3">
                {abandoned.map((c) => <ClaimRow key={c.session_id} claim={c} onResend={resend} resendingId={resendingId} />)}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
